import {
  CalendarMemberMapping,
  CalendarMemberMappingStorage,
} from '@/types/calendar-member-mapping';
import { ScopedAsyncStorage } from '@/lib/storage';

const STORAGE_KEY = '@calendar_member_mappings';
const STORAGE_VERSION = '1.0';

/**
 * Local storage for calendar-to-family-member mappings using AsyncStorage
 */
export class LocalCalendarMemberMappingStore {
  private readonly storage: ScopedAsyncStorage;

  /**
   * Create a new LocalCalendarMemberMappingStore instance
   * @param storage - Scoped storage instance
   */
  constructor(storage: ScopedAsyncStorage) {
    this.storage = storage;
  }

  private getDefaultStorage(): CalendarMemberMappingStorage {
    return {
      mappings: {},
      version: STORAGE_VERSION,
    };
  }

  /**
   * Load storage data from AsyncStorage
   */
  private async loadStorage(): Promise<CalendarMemberMappingStorage> {
    try {
      const data = await this.storage.getItem(STORAGE_KEY);
      if (!data) {
        return this.getDefaultStorage();
      }
      return JSON.parse(data);
    } catch (error) {
      console.error('Error loading calendar member mappings:', error);
      return this.getDefaultStorage();
    }
  }

  /**
   * Save storage data to AsyncStorage
   */
  private async saveStorage(storage: CalendarMemberMappingStorage): Promise<void> {
    await this.storage.setItem(STORAGE_KEY, JSON.stringify(storage));
  }

  /**
   * Get all calendar-member mappings
   */
  async getAll(): Promise<CalendarMemberMapping[]> {
    const storage = await this.loadStorage();
    return Object.values(storage.mappings);
  }

  /**
   * Get mapping for a specific calendar
   */
  async getByCalendarId(calendarId: string): Promise<CalendarMemberMapping | null> {
    const storage = await this.loadStorage();
    return storage.mappings[calendarId] || null;
  }

  /**
   * Set mapping for a calendar (creates or updates)
   */
  async setMapping(calendarId: string, memberIds: string[]): Promise<CalendarMemberMapping> {
    const storage = await this.loadStorage();

    const mapping: CalendarMemberMapping = {
      calendarId,
      memberIds,
      updatedAt: new Date().toISOString(),
    };

    storage.mappings[calendarId] = mapping;
    await this.saveStorage(storage);

    return mapping;
  }

  /**
   * Remove mapping for a calendar
   */
  async removeMapping(calendarId: string): Promise<boolean> {
    const storage = await this.loadStorage();

    if (!storage.mappings[calendarId]) return false;

    delete storage.mappings[calendarId];
    await this.saveStorage(storage);

    return true;
  }

  /**
   * Get all calendars mapped to a specific family member
   */
  async getCalendarsForMember(memberId: string): Promise<string[]> {
    const storage = await this.loadStorage();
    return Object.values(storage.mappings)
      .filter(m => m.memberIds.includes(memberId))
      .map(m => m.calendarId);
  }

  /**
   * Remove a family member from all mappings
   * Used when deleting a family member
   */
  async removeMemberFromAllMappings(memberId: string): Promise<void> {
    const storage = await this.loadStorage();
    let changed = false;

    for (const calendarId of Object.keys(storage.mappings)) {
      const mapping = storage.mappings[calendarId];
      const filteredIds = mapping.memberIds.filter(id => id !== memberId);

      if (filteredIds.length !== mapping.memberIds.length) {
        changed = true;
        if (filteredIds.length === 0) {
          delete storage.mappings[calendarId];
        } else {
          storage.mappings[calendarId] = {
            ...mapping,
            memberIds: filteredIds,
            updatedAt: new Date().toISOString(),
          };
        }
      }
    }

    if (changed) {
      await this.saveStorage(storage);
    }
  }
}
